import React, { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { collectDescendantIds, indexCategoryTree } from "../../../utils/categoryTreeUtils";
import { translateStoreCategory } from "../../../utils/storeUtils";
import "./CategoryTreeFilter.css";

function safeId(v) {
  if (!v) return "";
  if (typeof v === "object" && v._id) return String(v._id);
  return String(v);
}

function NodeRow({
  node,
  depth,
  expanded,
  onToggleExpand,
  selectedId,
  onSelect,
  labelFor,
  t,
}) {
  const id = safeId(node?._id);
  const hasChildren = Array.isArray(node?.children) && node.children.length > 0;
  const isSelected = selectedId === id;
  const isInactive = node?.isActive === false;
  const label = labelFor?.(node) || node?.label || "";

  return (
    <div
      className={`ctf-row${isSelected ? " is-selected" : ""}${isInactive ? " is-inactive" : ""}`}
      style={{ paddingInlineStart: `${10 + depth * 14}px` }}
    >
      {hasChildren ? (
        <button
          type="button"
          className={`ctf-expander${expanded ? " is-open" : ""}`}
          onClick={() => onToggleExpand(id)}
          aria-label={
            expanded
              ? t?.("store.treeCollapse", { defaultValue: "Collapse" })
              : t?.("store.treeExpand", { defaultValue: "Expand" })
          }
        >
          <i className="fa-solid fa-chevron-right" aria-hidden="true" />
        </button>
      ) : (
        <span className="ctf-expander-spacer" aria-hidden="true" />
      )}

      <button
        type="button"
        className="ctf-label"
        onClick={() => onSelect(node)}
        aria-pressed={isSelected}
      >
        {label}
      </button>
    </div>
  );
}

export default function CategoryTreeFilter({
  tree,
  selectedCategoryId,
  onChange,
  /** 0 = all branches start collapsed. Ancestors of the selected category still expand so the active row is visible. */
  defaultExpandedDepth = 0,
}) {
  const { t, i18n } = useTranslation("global");
  const isRtl = String(i18n.language || "").toLowerCase().startsWith("ar");
  const [open, setOpen] = useState(() => new Set());

  const treeIndex = useMemo(() => indexCategoryTree(tree), [tree]);
  const labelFor = useCallback(
    (node) => {
      if (!node) return "";
      const en = String(node?.label || "").trim();
      const ar = String(node?.arLabel || "").trim();
      // Prefer DB-provided labels; fall back to i18n map by slug (so Arabic works even when arLabel is empty).
      if (isRtl) return ar || translateStoreCategory(t, node) || en;
      return en || translateStoreCategory(t, node) || ar;
    },
    [isRtl, t]
  );

  React.useEffect(() => {
    const next = new Set();
    const walk = (nodes, depth) => {
      (nodes || []).forEach((n) => {
        const id = safeId(n?._id);
        if (defaultExpandedDepth > 0 && depth < defaultExpandedDepth && id) next.add(id);
        walk(n.children || [], depth + 1);
      });
    };
    walk(Array.isArray(tree) ? tree : [], 0);
    if (selectedCategoryId && treeIndex.parentById) {
      let pid = treeIndex.parentById.get(String(selectedCategoryId));
      let guard = 0;
      while (pid && guard < 30) {
        next.add(pid);
        pid = treeIndex.parentById.get(pid);
        guard += 1;
      }
    }
    setOpen(next);
  }, [tree, treeIndex, defaultExpandedDepth, selectedCategoryId]);

  const toggleExpand = useCallback((id) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelect = useCallback(
    (node) => {
      if (!node) return;
      const ids = collectDescendantIds(node);
      onChange?.({
        selectedId: safeId(node._id),
        includedIds: ids,
        selectedNode: node,
        treeIndex,
      });
    },
    [onChange, treeIndex]
  );

  const renderNode = (node, depth) => {
    const id = safeId(node?._id);
    const isExpanded = open.has(id);
    const hasChildren = Array.isArray(node?.children) && node.children.length > 0;
    return (
      <React.Fragment key={id || `${node?.slug || node?.label}-${depth}`}>
        <NodeRow
          node={node}
          depth={depth}
          expanded={isExpanded}
          onToggleExpand={toggleExpand}
          selectedId={selectedCategoryId}
          onSelect={handleSelect}
          labelFor={labelFor}
          t={t}
        />
        {hasChildren && isExpanded
          ? node.children.map((c) => renderNode(c, depth + 1))
          : null}
      </React.Fragment>
    );
  };

  return (
    <div className="ctf">
      <div className="ctf-header">
        <div className="ctf-title">
          {t("store.categoriesTitle", { defaultValue: "Categories" })}
        </div>
        <button
          type="button"
          className="ctf-clear"
          onClick={() => onChange?.({ selectedId: "", includedIds: [], selectedNode: null, treeIndex })}
          disabled={!selectedCategoryId}
        >
          {t("store.clear", { defaultValue: "Clear" })}
        </button>
      </div>
      <div className="ctf-list" role="tree">
        {(Array.isArray(tree) ? tree : []).map((n) => renderNode(n, 0))}
      </div>
    </div>
  );
}

